import { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../context/ToastContext';
import { useSiteLogo } from '../../context/SiteLogoContext';
import { BRAND } from '../../config/brand';
import './Auth.css';

const SITE_URL   = BRAND?.url || process.env.REACT_APP_SITE_URL || 'https://theluviajewels.com';
const BRAND_NAME = BRAND?.name || 'Luvia';

function Login() {
  const [formData, setFormData] = useState({
    email: '',
    password: '',
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(true);
  const { login, googleLogin, isAuthenticated } = useAuth();
  const { showToast } = useToast();
  const { resolvedLogoUrl, logoAlt } = useSiteLogo();
  const navigate = useNavigate();
  const location = useLocation();

  // Check for redirect query parameter (e.g., ?redirect=cart or ?returnUrl=/product/xyz#reviews)
  const searchParams = new URLSearchParams(location.search);
  const redirectParam = searchParams.get('redirect');
  const returnUrlParam = searchParams.get('returnUrl');
  const from = location.state?.from?.pathname
    || (returnUrlParam ? decodeURIComponent(returnUrlParam) : (redirectParam ? `/${redirectParam}` : '/'));

  // Show success message when redirected from reset password
  useEffect(() => {
    const message = location.state?.message;
    if (message && typeof showToast === 'function') {
      showToast(message, 'success');
      navigate(location.pathname + location.search, { replace: true, state: {} });
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps -- run once on mount when state has message

  // Show a clear message when session has expired
  useEffect(() => {
    if (location.state?.reason === 'SESSION_EXPIRED') {
      setError('Your session has expired. Please sign in again to continue.');
    }
  }, [location.state]);

  useEffect(() => {
    if (isAuthenticated) {
      navigate(from, { replace: true });
    }
  }, [isAuthenticated, navigate, from]);

  // Retry handler
  const handleRetry = () => {
    setError('');
    setLoading(false);
    // Form will be re-submitted when user clicks submit again
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Manual validation
    if (!formData.email || !/\S+@\S+\.\S+/.test(formData.email)) {
      setError('Please enter a valid email address.');
      return;
    }
    if (!formData.password || formData.password.length < 6) {
      setError('Password must be at least 6 characters.');
      return;
    }

    setLoading(true);

    const result = await login(formData.email, formData.password, rememberMe);

    if (result.success) {
      // Use backend redirectPath if provided (for admin redirect), otherwise use from
      const finalRedirect = result.redirectPath || from;
      const firstName = result.user?.firstName || result.user?.name?.split?.(' ')?.[0] || '';

      if (redirectParam === 'cart') {
        showToast(firstName
          ? `Welcome back, ${firstName}! Your bag is ready.`
          : 'Welcome back! Your bag is ready.', 'success');
      } else if (firstName) {
        showToast(`Good to see you again, ${firstName}.`, 'success');
      }

      navigate(finalRedirect, { replace: true });
      return; // Don't update state after navigation
    }

    // Map backend error codes to user-friendly messages
    const errorMessages = {
      'INVALID_CREDENTIALS': 'Incorrect email or password.',
      'ACCOUNT_LOCKED': 'Account temporarily locked. Try again in 15 minutes.',
      'RATE_LIMITED': 'Too many attempts. Please wait a moment.',
      'ACCOUNT_NOT_VERIFIED': 'Please verify your email before signing in.',
      'NETWORK_ERROR': 'Connection failed. Check your internet and try again.',
      'TIMEOUT_ERROR': 'Request timed out. Please try again.',
    };
    setError(errorMessages[result.code] || result.message || 'Sign in failed. Please try again.');
    setLoading(false);
  };

  const handleGoogleLogin = async () => {
    try {
      setLoading(true);
      setError('');
      // googleLogin redirects, so we can't await it, but we can handle errors
      googleLogin();
    } catch (error) {
      setError('Google sign in failed. Please try again.');
      setLoading(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>Sign In | {BRAND_NAME}</title>
        <meta name="description" content={`Sign in to ${BRAND_NAME} for faster checkout and exclusive access.`} />
        <meta name="robots" content="noindex, follow" />
        <link rel="canonical" href={`${SITE_URL}/login`} />
      </Helmet>
      
      <div className="auth-page">
        <div className="auth-container">
          <div className="auth-header">
            <Link to="/" className="auth-logo" aria-label={`${BRAND_NAME} Home`}>
              <img src={resolvedLogoUrl} alt={logoAlt || BRAND_NAME} className="auth-logo-image" width="120" height="40" />
            </Link>
            <h1 className="auth-title">Welcome Back</h1>
            <p className="auth-subtitle">Sign in for faster checkout</p>
          </div>

        {error && (
          <div id="auth-error-message" className="auth-error" role="alert" aria-live="assertive">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="10" />
              <line x1="12" y1="8" x2="12" y2="12" />
              <line x1="12" y1="16" x2="12.01" y2="16" />
            </svg>
            <span>{error}</span>
            {(error.includes('timeout') || error.includes('Network error')) && (
              <button
                type="button"
                className="auth-error-retry"
                onClick={handleRetry}
                aria-label="Retry login"
              >
                Retry
              </button>
            )}
          </div>
        )}

        <form onSubmit={handleSubmit} className="auth-form" noValidate>
          <div className="auth-form-group">
            <label htmlFor="email" className="auth-label">Email</label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              className="auth-input"
              placeholder="your@email.com"
              required
              autoComplete="email"
              autoFocus
              inputMode="email"
              aria-describedby={error ? 'auth-error-message' : undefined}
              aria-invalid={!!error}
            />
          </div>

          <div className="auth-form-group auth-form-group--password">
            <label htmlFor="password" className="auth-label">Password</label>
            <div className="auth-input-wrapper">
              <input
                type={showPassword ? 'text' : 'password'}
                id="password"
                name="password"
                value={formData.password}
                onChange={handleChange}
                className="auth-input"
                placeholder="Enter your password"
                required
                autoComplete="current-password"
                minLength="6"
                aria-describedby={error ? 'auth-error-message' : undefined}
                aria-invalid={!!error}
              />
              <button
                type="button"
                className="auth-input-toggle"
                onClick={() => setShowPassword(prev => !prev)}
                aria-label={showPassword ? 'Hide password' : 'Show password'}
              >
                {showPassword ? (
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94" />
                    <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19" />
                    <line x1="1" y1="1" x2="23" y2="23" />
                  </svg>
                ) : (
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                    <circle cx="12" cy="12" r="3" />
                  </svg>
                )}
              </button>
            </div>
          </div>

          <div className="auth-form-options">
            <label className="auth-checkbox-label">
              <input
                type="checkbox"
                checked={rememberMe}
                onChange={e => setRememberMe(e.target.checked)}
                className="auth-checkbox"
              />
              <span>Keep me signed in</span>
            </label>
            <Link to="/forgot-password" className="auth-link">
              Forgot password?
            </Link>
          </div>

          <button
            type="submit"
            className="auth-button auth-button--primary"
            disabled={loading}
          >
            {loading ? (
              <>
                <svg className="auth-spinner" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 12a9 9 0 1 1-6.219-8.56" />
                </svg>
                Signing in...
              </>
            ) : 'Sign In'}
          </button>
        </form>

        <div className="auth-divider">
          <span>OR</span>
        </div>

        <button
          type="button"
          onClick={handleGoogleLogin}
          className="auth-button auth-button--google"
          disabled={loading}
        >
          <svg width="20" height="20" viewBox="0 0 24 24">
            <path
              fill="#4285F4"
              d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
            />
            <path
              fill="#34A853"
              d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
            />
            <path
              fill="#FBBC05"
              d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
            />
            <path
              fill="#EA4335"
              d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
            />
          </svg>
          Continue with Google
        </button>

          <div className="auth-trust">
            <span className="auth-trust-item">
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                <path d="M7 11V7a5 5 0 0 1 10 0v4" />
              </svg>
              Secure login
            </span>
            <span className="auth-trust-divider">·</span>
            <span className="auth-trust-item">
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="20 6 9 17 4 12" />
              </svg>
              Fast checkout
            </span>
          </div>

          <p className="auth-footer">
            New to {BRAND_NAME}?{' '}
            <Link to="/register" className="auth-link auth-link--primary">
              Create account
            </Link>
          </p>
        </div>
      </div>
    </>
  );
}

export default Login;
